import React, { useState, useEffect, useCallback } from 'react'
import c from 'classnames'
import { connect } from 'react-redux'
import get from 'lodash/get'
import set from 'lodash/set'
import cloneDeep from 'lodash/clone'
import isEmpty from 'lodash/isEmpty'
import uniq from 'lodash/uniq'

import Button from 'discada/dist/Button'

import { INTENTS_LABELS } from 'reducers/quoteReducers'

import {
  getCustomer,
  getRooftopName,
  getRooftopSalesPhone,
  getProfileOk,
  getIsKnownCustomerFromState,
  getCustomerVerifications,
  getMeaningfulDiff,
  areProgramsDirty,
} from 'selectors/appStatusSelectors'
import { resetCustomerTag } from 'actions/naughtyActions'
import { addToPageState } from 'actions/pageStateActions'

import {
  ReactionLocation,
  ReactionCredit,
  ReactionContact,
  ReactionSaved,
  ReactionSellCar,
  ReactionNoOwn,
} from 'components/widgets/Reactions/ReactionSection'

import { SqueezeTrades } from 'components/Quote/Trades'
import { SimpleRebates as StupidRebates } from 'components/Quote/SimpleRebates'

import Profile from 'components/widgets/Profile'
import { ConnectedDistractor as Distractor } from 'components/widgets/Distractor'

import { refreshPrograms } from 'actions/programActions'

import { hydrateCustomer, setProgramsDirty } from 'actions/appStatusActions'
import {
  getQueryValue,
  getActiveQuery,
  getActiveParams,
  getParamValue,
} from 'selectors/pageStateSelectors'

import Disclaimer from 'components/widgets/Disclaimer'

import { getQuoteDirty } from 'selectors/quoteSelectors'
import { changeCurrentQuote } from 'actions/programActions'

import ReactionBubble from './ReactionBubble'

import { isCustomerKnown } from 'utils'
import { areTradesConfirmed } from 'utils/trade'

import './index.scss'

export const JACKET_STEPS = [
    {
      step: 'location',
      label: 'Your Location',
    },
    {
      step: 'trades',
      label: 'Your Trade',
    },
    {
      step: 'rebates',
      label: 'Your Incentives',
    },
    {
      step: 'credit',
      label: 'Your Credit',
    },
    {
      step: 'contact',
      label: 'Save',
    },
    {
      step: 'getPayments',
      hidden: true,
      label: 'Get Payments',
    },
    {
      step: 'sellMyTrade',
      hidden: true,
      label: 'Sell your Car',
    },
    {
      step: 'noOwn',
      hidden: true,
      label: 'Sell your Car',
    },
    {
      step: 'saved',
      hidden: true,
      label: 'Thanks!',
    },
  ],
  SQUEEZE_TIMER = 45 * 1000

export function ReactionFormPreso(props) {
  let {
      reaction,
      children,
      step,
      customer,
      addToPageState,
      hydrateCustomer,
      gettingPrograms,
      close = () => {},
      profileOk,
      isKnownCustomer,
      tradesConfirmed,
      setDirty,
    } = props,
    additionalControl,
    submitIcon

  let [formState, setFormState] = useState({
      customer,
      previousCustomer: { ...customer },
    }),
    [jacketStep, setJacketStep] = useState(step),
    tag = useCallback(
      (createChangeEvent = true) => {
        hydrateCustomer(
          {
            ...formState.customer,
            lastSqueeze: new Date().getTime(),
          },
          { createChangeEvent },
        )
      },
      [formState.customer, hydrateCustomer],
    ),
    commonFieldProps = {
      size: 'small',
      rounded: true,
      onBlur: tag,
    },
    getValue = (key, defaultVal = '') => get(formState, key, defaultVal),
    updateFormState = useCallback(
      (newData = {}, autoTag, createChangeEvent) => {
        let newFormState = cloneDeep(formState || {})

        Object.keys(newData).forEach(path =>
          set(newFormState, path, newData[path]),
        )

        setFormState(newFormState)

        autoTag && tag(createChangeEvent)
      },
      [formState, tag],
    ),
    setValue = (path, autoTag) => ({ target }) =>
      updateFormState({ [path]: target.value }, autoTag),
    checkStep = s => s === jacketStep || jacketStep === 'all',
    exit = () => {
      tag()
      close()
    },
    getStep = (direction = 1) => {
      let currentIndex = JACKET_STEPS.findIndex(js => js.step === jacketStep),
        limiter = direction > 0 ? Math.min : Math.max,
        limitScrew = direction > 0 ? JACKET_STEPS.length - 1 : 0,
        nextIndex = limiter(currentIndex + direction, limitScrew)

      let ret =
        nextIndex === currentIndex || get(JACKET_STEPS[nextIndex], 'hidden')
          ? []
          : [JACKET_STEPS[nextIndex], nextIndex]

      return ret
    },
    goToStep = (direction = 1) => {
      let [nextStep] = getStep(direction)

      if (isEmpty(nextStep) && direction > 0) {
        exit(formState, props)
      } else if (!isEmpty(step)) {
        addToPageState({ reaction, step: nextStep.step })
      }
    },
    goToTrade = () => {
      addToPageState({ reaction, step: 'trades' })
    },
    goToSaved = () => {
      addToPageState({ reaction, step: 'saved' })
    },
    onSubmit = exit,
    submitOk = () => {
      let tradeSubmitOk =
          !checkStep('trades') || (checkStep('trades') && tradesConfirmed),
        noSubmit = ['noOwn', 'getPayments'].includes(jacketStep)

      return !noSubmit && tradeSubmitOk
    }

  useEffect(() => {
    setFormState({
      previousCustomer: { ...formState.customer },
      customer: { ...formState.customer, ...customer },
    })
  }, [customer])

  useEffect(() => {
    !isEmpty(
      getMeaningfulDiff(formState.customer, formState.previousCustomer),
    ) && setDirty(true)
  }, [formState])

  // tag customer on step change and modal close
  useEffect(() => {
    setJacketStep(props.step)
  }, [props.step, props.reaction]) // eslint-disable-line

  if (!profileOk) return null

  let submitContent = (
    <>
      Start <strong>Shopping</strong>!
    </>
  )

  if (checkStep('location')) {
    submitContent = (
      <>
        Get my <strong>fees and taxes</strong>!
      </>
    )
    submitIcon = 'map-marker-alt'
  }

  if (checkStep('rebates')) {
    submitContent = (
      <>
        See my <strong>savings</strong>!
      </>
    )
    submitIcon = 'newspaper'
  }

  if (checkStep('credit')) {
    submitContent = (
      <>
        Get my <strong>rates</strong>!
      </>
    )
    submitIcon = 'credit-card'
  }

  if (checkStep('sellMyTrade')) {
    submitContent = (
      <>
        Get my <strong>Cash Offer</strong>
      </>
    )
    additionalControl = (
      <div className="control">
        <Button icon="chevron-left" text size="small" onClick={goToTrade}>
          Back to Trade
        </Button>
      </div>
    )
    submitIcon = 'money-bill-wave'
    submitOk = () => isCustomerKnown(formState.customer)
    onSubmit = () => {
      updateFormState({ 'customer.sellMyTrade': true }, true)
      goToSaved()
    }
  }

  if (checkStep('trades')) {
    submitContent = <>See Payments with my Equity</>
    submitIcon = 'car'
  }

  if (checkStep('saved')) {
    additionalControl = (
      <div className="control">
        <Button icon="chevron-left" text size="small" onClick={goToTrade}>
          Back to Trade
        </Button>
      </div>
    )
    submitContent = <>See Payments with my Equity</>
    submitIcon = 'car'
  }

  return (
    <div className="reaction-form">
      <div className="reaction-form-header">
        {INTENTS_LABELS[reaction] && (
          <ReactionBubble size="small" reaction={reaction} />
        )}
      </div>
      <Profile mini />
      <div className="reaction-form-inner">
        {checkStep('location') && (
          <ReactionLocation
            formState={formState}
            fieldProps={commonFieldProps}
            zip={getValue('customer.zipCode')}
            onZipChange={zip => {
              updateFormState(
                {
                  'customer.zipCode': zip,
                  'customer.taxRegionLabel': null,
                  'customer.taxRegionId': null,
                  'customer.location': { zipCode: zip, taxRegionId: null },
                },
                true,
                zip.length === 5,
              )
            }}
            onGeoSelect={loc => {
              updateFormState(
                {
                  'customer.zipCode': loc.zipCode,
                  'customer.location': loc,
                },
                true,
              )
            }}
            onTaxRegionSelect={taxRegion => {
              let {
                zipCode,
                taxRegionId,
                stateFeeTax,
                taxRegionLabel,
              } = taxRegion

              taxRegionId &&
                updateFormState(
                  {
                    'customer.zipCode': zipCode,
                    'customer.taxRegionId': taxRegionId,
                    'customer.stateFeeTax': stateFeeTax,
                    'customer.taxRegionLabel': taxRegionLabel,
                    'customer.location': {
                      zipCode,
                      taxRegionId,
                      taxRegionLabel,
                      stateFeeTax,
                    },
                  },
                  true,
                )
            }}
          />
        )}
        {checkStep('trades') && <SqueezeTrades />}
        {checkStep('getPayments') && (
          <Distractor
            reactionMode
            onComplete={close}
            finishOk={!gettingPrograms}
          />
        )}
        {checkStep('rebates') && (
          <StupidRebates
            {...commonFieldProps}
            showAll
            noRebates={get(formState, 'customer.hasNoRebates')}
            tagCustomer={(vId, qId, _, { hasNoRebates } = {}) => {
              updateFormState(
                {
                  'customer.hasNoRebates': hasNoRebates,
                  'customer.selectedRebateCategories': [],
                },
                true,
              )
            }}
            selectedCategories={formState.customer.selectedRebateCategories}
            onChange={cat => {
              let newCats = cloneDeep(
                  get(formState, 'customer.selectedRebateCategories', []),
                ),
                catIdx = newCats.findIndex(c => c.ID === cat.ID)

              catIdx > -1 ? newCats.splice(catIdx, 1) : newCats.push(cat)

              updateFormState(
                {
                  'customer.selectedRebateCategories': uniq(newCats),
                  'customer.hasNoRebates': false,
                },
                true,
              )
            }}
          />
        )}
        {checkStep('credit') && (
          <ReactionCredit
            createChangeEvent
            value={getValue('customer.creditScore', null)}
            onChange={v => {
              updateFormState(
                {
                  'customer.creditScore': v,
                },
                true,
              )
            }}
          />
        )}
        {typeof children === 'function' && (
          <div className="reaction-form-child-fields">
            {children({ setValue: setFormState, getValue }, props)}
          </div>
        )}
        {checkStep('noOwn') && (
          <ReactionNoOwn
            NoOwn
            {...props}
            createChangeEvent
            formState={formState}
            getValue={getValue}
            setValue={setValue}
            fieldProps={commonFieldProps}
            onClick={() => addToPageState({ reaction, step: 'trades' })}
          />
        )}
        {(checkStep('contact') ||
          (!isKnownCustomer &&
            !(
              checkStep('getPayments') ||
              checkStep('saved') ||
              checkStep('contact') ||
              checkStep('sellMyTrade')
            ))) && (
          <ReactionContact
            {...props}
            createChangeEvent
            formState={formState}
            getValue={getValue}
            setValue={setValue}
            fieldProps={commonFieldProps}
          />
        )}
        {checkStep('saved') && <ReactionSaved {...props} />}
        {checkStep('sellMyTrade') && (
          <ReactionSellCar
            {...props}
            createChangeEvent
            formState={formState}
            getValue={getValue}
            setValue={setValue}
            fieldProps={commonFieldProps}
          />
        )}
      </div>

      {submitOk() && (
        <div className={c('reaction-form-controls')}>
          <div className="field has-addons submit-buttons">
            {jacketStep && !isEmpty(getStep(-1)) && (
              <div className="control squeeze-jacket-step squeeze-jacket-step-mobile">
                <Button
                  icon="chevron-left"
                  size="small"
                  onClick={() => goToStep(-1)}
                />
              </div>
            )}
            <div className="control">
              <Button
                text
                size="small"
                onClick={onSubmit}
                disabled={!submitOk()}
              >
                Close
              </Button>
            </div>
            <div className="control">
              <Button
                success
                size="small"
                icon={submitIcon || 'hand-pointer'}
                onClick={onSubmit}
                disabled={!submitOk()}
              >
                {submitContent}
              </Button>
            </div>
            {jacketStep &&
              !isEmpty(getStep()) &&
              !get(getStep(), '[0].hidden') && (
                <div className="control squeeze-jacket-step squeeze-jacket-step-mobile">
                  <Button
                    icon="chevron-right"
                    size="small"
                    onClick={() => goToStep()}
                  />
                </div>
              )}
          </div>
          <div className="field has-addons util-buttons">
            {jacketStep && !isEmpty(getStep(-1)) && (
              <div className="control squeeze-jacket-step">
                <Button
                  text
                  icon="chevron-left"
                  size="small"
                  onClick={() => goToStep(-1)}
                >
                  {get(getStep(-1), '[0].label')}
                </Button>
              </div>
            )}
            {jacketStep &&
              !isEmpty(getStep()) &&
              !get(getStep(), '[0].hidden') && (
                <div className="control squeeze-jacket-step">
                  <Button
                    reverse
                    text
                    icon="chevron-right"
                    size="small"
                    onClick={() => goToStep()}
                  >
                    {get(getStep(), '[0].label')}
                  </Button>
                </div>
              )}
            {additionalControl}
          </div>
        </div>
      )}
      <Disclaimer className="reaction-form-disclaimer" />
    </div>
  )
}

function mapStateToProps(state, op) {
  let customer = getCustomer(state),
    vehicleId = getParamValue(state, 'id'),
    quoteId = getQueryValue(state, 'quoteId')

  return {
    ...op,
    customer,
    rooftopName: getRooftopName(state),
    rooftopPhone: getRooftopSalesPhone(state),
    isVDP: vehicleId && quoteId,
    formState: { customer },
    tradesConfirmed: areTradesConfirmed(state),
    isKnownCustomer: getIsKnownCustomerFromState(state),
    verifications: getCustomerVerifications(state),
    profileOk: getProfileOk(state),
    query: getActiveQuery(state),
    params: getActiveParams(state),
    dirty: areProgramsDirty(state, vehicleId, quoteId),
  }
}

export const ReactionForm = connect(
  mapStateToProps,
  {
    hydrateCustomer,
    addToPageState,
    resetCustomerTag,
    refreshPrograms,
    changeCurrentQuote,
    setDirty: setProgramsDirty,
  },
)(ReactionFormPreso)
